// @fluxora/auth — seal a login password before it leaves the browser.
//
// The CRM login used to POST `{ email, password }` as JSON, which put the
// password in plain sight in DevTools, saved HAR files and any proxy or log
// that captures bodies. A security audit flags exactly that. Hashing would not
// help (the hash becomes the password), so instead we ENCRYPT it to the
// backend's public key, bound to a one-time challenge the backend issued:
//
//   1. GET  <challengeUrl>  → { challenge, publicKey (RSA JWK) }
//   2. AES-256-GCM over the password (fresh key + IV every time), with
//      additional-authenticated-data `${challenge}\n${email}`; the AES key is
//      wrapped with RSA-OAEP-256.
//   3. POST <loginUrl>      → { email, challenge, encryptedPassword }
//
// `encryptedPassword` = `v1.<wrappedKey>.<iv>.<ciphertext+tag>` (base64url).
// Hybrid rather than raw RSA because RSA-2048/OAEP-256 only carries 190 bytes.
// The server half is Accounts `core/security/login-credential-crypto.ts`; the
// two MUST agree on this format.
//
// WebCrypto only — no dependency. It exists only in a secure context (HTTPS or
// localhost); without it we refuse to log in rather than fall back to
// plaintext, which would silently reintroduce the finding.

/** What `GET /v1/auth/login-challenge` returns. */
export interface LoginChallenge {
  readonly challenge: string;
  readonly publicKey: JsonWebKey;
}

/** The body `POST /v1/auth/login` now takes — no `password` key, ever. */
export interface SealedLoginBody {
  readonly email: string;
  readonly challenge: string;
  readonly encryptedPassword: string;
}

export const LOGIN_ENVELOPE_VERSION = 'v1';

function toBase64Url(bytes: ArrayBuffer | Uint8Array): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let binary = '';
  for (const b of view) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/u, '');
}

/** True when this browser context can seal a password (secure context + WebCrypto). */
export function canSealPassword(): boolean {
  return typeof globalThis.crypto !== 'undefined' && typeof globalThis.crypto.subtle !== 'undefined';
}

/** Encrypt `password` for `email` under a server-issued challenge. */
export async function sealPassword(
  challenge: LoginChallenge,
  email: string,
  password: string,
): Promise<SealedLoginBody> {
  if (!canSealPassword()) {
    throw new Error('This browser cannot sign in securely here. Use an up-to-date browser over HTTPS.');
  }
  const { subtle } = globalThis.crypto;
  const encoder = new TextEncoder();
  const rsaKey = await subtle.importKey(
    'jwk',
    // Only the members importKey needs — `kid`/`use` from the server are fine,
    // but a stray `key_ops` that disagrees with our usage would make it throw.
    { kty: challenge.publicKey.kty, n: challenge.publicKey.n, e: challenge.publicKey.e, alg: 'RSA-OAEP-256', ext: true },
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['encrypt'],
  );
  const aesKey = await subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt']);
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
  const box = await subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: encoder.encode(`${challenge.challenge}\n${email}`) },
    aesKey,
    encoder.encode(password),
  );
  const wrappedKey = await subtle.encrypt({ name: 'RSA-OAEP' }, rsaKey, await subtle.exportKey('raw', aesKey));
  return {
    email,
    challenge: challenge.challenge,
    encryptedPassword: [LOGIN_ENVELOPE_VERSION, toBase64Url(wrappedKey), toBase64Url(iv), toBase64Url(box)].join('.'),
  };
}

/** Narrow an unknown JSON body to a {@link LoginChallenge}. */
export function isLoginChallenge(value: unknown): value is LoginChallenge {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as { challenge?: unknown; publicKey?: unknown };
  if (typeof v.challenge !== 'string' || v.challenge.length === 0) return false;
  if (typeof v.publicKey !== 'object' || v.publicKey === null) return false;
  const k = v.publicKey as { kty?: unknown; n?: unknown; e?: unknown };
  return k.kty === 'RSA' && typeof k.n === 'string' && typeof k.e === 'string';
}
